import { MainLayout } from '@/components/layout/MainLayout';
import { JobsTable } from '@/components/jobs/JobsTable';
import { Pagination } from '@/components/common/Pagination';
import { JobSchedulingForm } from '@/components/jobs/JobSchedulingForm';
import { JobsListPanel } from '@/components/jobs/JobsListPanel';
import { GanttChart } from '@/components/jobs/GanttChart';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getJobs, deleteJob, duplicateJob, createScheduledJob, updateScheduledJob, type JobScheduleData } from '@/lib/api/jobs';
import type { Job } from '@/lib/api/jobs';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { LargeTitleHeader, SegmentedControl, PullToRefresh } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

// localStorage keys for persisted filters
const LS_STATUS = 'jobs_statusFilter';
const LS_SORT_COL = 'jobs_sortColumn';
const LS_SORT_DIR = 'jobs_sortDirection';
const LS_VIEW = 'jobs_viewMode';

export const JobsListPage = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem(LS_STATUS) || 'All');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'gantt'>(() => (localStorage.getItem(LS_VIEW) as 'list' | 'gantt') || 'list');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>(() => localStorage.getItem(LS_SORT_COL) || 'jobNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => (localStorage.getItem(LS_SORT_DIR) as 'asc' | 'desc') || 'desc');
  const [showNewJobOverlay, setShowNewJobOverlay] = useState(false);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [editingScheduleJob, setEditingScheduleJob] = useState<Job | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDeleteJob, setConfirmDeleteJob] = useState<Job | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const [quickFilter, setQuickFilter] = useState<string>('');

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, sortColumn, sortDirection]);

  // Persist filters & sort to localStorage (#1)
  useEffect(() => { localStorage.setItem(LS_STATUS, statusFilter); }, [statusFilter]);
  useEffect(() => { localStorage.setItem(LS_SORT_COL, sortColumn); }, [sortColumn]);
  useEffect(() => { localStorage.setItem(LS_SORT_DIR, sortDirection); }, [sortDirection]);
  useEffect(() => { localStorage.setItem(LS_VIEW, viewMode); }, [viewMode]);

  // Keyboard shortcuts: "n" = new job, "/" = focus search, Esc = close overlay (#2)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable;
    if (e.key === 'Escape') {
      if (showNewJobOverlay) setShowNewJobOverlay(false);
      if (showEditScheduleModal) { setShowEditScheduleModal(false); setEditingScheduleJob(null); }
      return;
    }
    if (isInput) return;
    if (e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      setShowNewJobOverlay(true);
    } else if (e.key === '/') {
      e.preventDefault();
      searchInputRef.current?.focus();
    }
  }, [showNewJobOverlay, showEditScheduleModal]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await getJobs({
        search: searchTerm,
        status: statusFilter === 'All' ? undefined : statusFilter,
        page: currentPage,
        limit: itemsPerPage,
      });
      setJobs(response.jobs);
      setTotalItems(response.total || response.jobs.length);
      
      // Client-side sorting
      const sorted = [...response.jobs].sort((a, b) => {
        const aVal = a[sortColumn as keyof Job];
        const bVal = b[sortColumn as keyof Job];
        
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        return sortDirection === 'asc'
          ? (aVal > bVal ? 1 : -1)
          : (bVal > aVal ? 1 : -1);
      });
      
      setJobs(sorted);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      setLoadError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleEdit = (job: Job) => {
    // Open scheduling form for editing
    setEditingScheduleJob(job);
    setShowEditScheduleModal(true);
  };

  const handleEditSchedule = (job: Job) => {
    // Same as handleEdit - kept for compatibility
    setEditingScheduleJob(job);
    setShowEditScheduleModal(true);
  };



  const handleDuplicate = async (job: Job) => {
    try {
      const result = await duplicateJob(job.id.toString());
      toast.success(`Job duplicated — new job #${result.job_number}`);
      loadJobs();
    } catch (error) {
      console.error('Failed to duplicate job:', error);
      toast.error('Failed to duplicate job');
    }
  };

  const handleDelete = (job: Job) => {
    setConfirmDeleteJob(job);
  };

  const confirmDeleteJobAction = async () => {
    if (!confirmDeleteJob) return;
    const job = confirmDeleteJob;
    setConfirmDeleteJob(null);
    try {
      await deleteJob(job.id.toString());
      toast.success('Job deleted successfully');
      loadJobs();
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job. It may have associated quotes or schedules.');
    }
  };

  const handleNewJobSubmit = async (data: JobScheduleData) => {
    try {
      setCreating(true);
      const result = await createScheduledJob(data);
      setShowNewJobOverlay(false);
      toast.success(`Job scheduled — ${result.taskIds.length} task${result.taskIds.length !== 1 ? 's' : ''} created`);
      await loadJobs();
      navigate(`/jobs/${result.jobId}`);
    } catch (error) {
      console.error('Failed to create scheduled job:', error);
      toast.error('Failed to create job. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateJobSchedule = async (data: JobScheduleData) => {
    if (!editingScheduleJob) return;

    try {
      setCreating(true);
      const result = await updateScheduledJob(editingScheduleJob.id.toString(), data);
      setShowEditScheduleModal(false);
      setEditingScheduleJob(null);
      toast.success(`Schedule updated — ${result.taskIds.length} task${result.taskIds.length !== 1 ? 's' : ''} synced`);
      await loadJobs();
    } catch (error) {
      console.error('Failed to update job schedule:', error);
      toast.error('Failed to update job schedule. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const exportCsv = (jobsToExport?: Job[]) => {
    const source = jobsToExport ?? jobs;
    const rows = [
      ['Job #', 'Title', 'Client', 'Status', 'Crew', 'Start Date', 'End Date'],
      ...source.map(j => [
        j.jobNumber ?? '',
        j.title ?? '',
        j.client ?? '',
        j.status ?? '',
        j.crew ?? '',
        j.startDate ?? '',
        j.estimatedEndDate ?? '',
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `jobs-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const exportSelectedCsv = () => {
    const selected = jobs.filter(j => selectedJobIds.has(j.id));
    if (selected.length === 0) return;
    exportCsv(selected);
    toast.success(`Exported ${selected.length} job${selected.length !== 1 ? 's' : ''}`);
  };

  const clearSelection = () => setSelectedJobIds(new Set());

  // Apply quick-filter chips on top of server-side filtering (#23)
  const displayedJobs = jobs.filter(j => {
    if (!quickFilter) return true;
    if (quickFilter === 'on hold') return j.status?.toLowerCase() === 'on hold';
    if (quickFilter === 'overdue') {
      const end = j.estimatedEndDate ? new Date(j.estimatedEndDate) : null;
      return end !== null && end < new Date() && j.status?.toLowerCase() !== 'completed';
    }
    if (quickFilter === 'no_crew') return !j.crew;
    if (quickFilter === 'this_week') {
      const now = new Date();
      const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay());
      const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 6);
      const start = j.startDate ? new Date(j.startDate) : null;
      return start !== null && start >= startOfWeek && start <= endOfWeek;
    }
    return true;
  });

  type StatusOption = 'All' | 'In Progress' | 'Scheduled' | 'Completed';
  const statusOptions: { value: StatusOption; label: string }[] = [
    { value: 'All', label: 'All' },
    { value: 'In Progress', label: 'Active' },
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'Completed', label: 'Done' },
  ];

  return (
    <MainLayout
      clientListPanel={<JobsListPanel onNewJob={() => setShowNewJobOverlay(true)} />}
    >
      <div className="flex flex-col gap-4">
        {/* Load Error Banner */}
        {loadError && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-sm text-red-700">
              <span className="material-symbols-outlined text-base">error</span>
              <span>{loadError}</span>
            </div>
            <button onClick={loadJobs} className="text-sm font-bold text-red-700 hover:underline whitespace-nowrap">Retry</button>
          </div>
        )}

        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Jobs"
          subtitle="Construction Projects"
          actions={
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center justify-center h-9 w-9 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  title="List view"
                >
                  <span className="material-symbols-outlined text-lg">view_list</span>
                </button>
                <button
                  onClick={() => setViewMode('gantt')}
                  className={`flex items-center justify-center h-9 w-9 transition-colors ${viewMode === 'gantt' ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  title="Gantt chart"
                >
                  <span className="material-symbols-outlined text-lg">view_timeline</span>
                </button>
              </div>
              <button
                onClick={() => exportCsv()}
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                title="Export CSV"
              >
                <span className="material-symbols-outlined text-lg">download</span>
              </button>
              <button
                className="flex cursor-pointer items-center justify-center gap-1.5 overflow-hidden rounded-lg h-9 px-3 bg-primary text-white text-sm font-bold leading-normal hover:opacity-90 transition-opacity"
                onClick={() => setShowNewJobOverlay(true)}
                title="New Job (N)"
              >
                <span className="material-symbols-outlined text-base">add</span>
                <span className="hidden sm:inline truncate">New Job</span>
                <kbd className="hidden lg:inline-flex items-center justify-center bg-white/20 rounded px-1 text-xs font-mono ml-1">N</kbd>
              </button>
            </div>
          }
        >
          {/* Search + status filter in the header's children */}
          <div className="flex flex-wrap items-center gap-3 pb-1">
            <div className="flex-1 min-w-[180px] max-w-sm">
              <label className="relative flex flex-col">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base">search</span>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border-gray-200 bg-gray-50 h-9 placeholder:text-gray-500 pl-9 pr-9 text-sm font-normal leading-normal"
                  placeholder="Search jobs…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setSearchTerm(''); (e.target as HTMLInputElement).blur(); } }}
                />
              </label>
            </div>
            <SegmentedControl<StatusOption>
              options={statusOptions}
              value={(statusFilter as StatusOption) || 'All'}
              onChange={(v) => setStatusFilter(v)}
              size="sm"
              fullWidth={false}
            />
          </div>
        </LargeTitleHeader>

        {/* Quick-filter chips (#23) */}
        {viewMode === 'list' && (
          <div className="flex flex-wrap gap-2 -mt-2">
            {[
              { label: 'On Hold', value: 'on hold', icon: 'pause_circle' },
              { label: 'Overdue', value: 'overdue', icon: 'schedule' },
              { label: 'No Crew', value: 'no_crew', icon: 'group_off' },
              { label: 'This Week', value: 'this_week', icon: 'event' },
            ].map(chip => (
              <button
                key={chip.value}
                onClick={() => setQuickFilter(f => f === chip.value ? '' : chip.value)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${quickFilter === chip.value ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary/40 hover:bg-primary/5'}`}
              >
                <span className="material-symbols-outlined text-xs">{chip.icon}</span>
                {chip.label}
              </button>
            ))}
            {quickFilter && (
              <button onClick={() => setQuickFilter('')} className="flex items-center gap-0.5 px-2 py-1 text-xs text-gray-400 hover:text-gray-600">
                <span className="material-symbols-outlined text-xs">close</span>Clear
              </button>
            )}
          </div>
        )}

        {/* Gantt Chart */}
        {viewMode === 'gantt' && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <GanttChart
              jobs={jobs.map(j => ({
                id: j.id,
                job_number: j.jobNumber,
                title: j.title,
                client: j.client,
                status: j.status,
                start_date: j.startDate ?? null,
                end_date: j.estimatedEndDate ?? null,
              }))}
            />
          </div>
        )}

        {/* Jobs Table — wrapped with PullToRefresh for mobile */}
        {viewMode === 'list' && (
          <PullToRefresh onRefresh={loadJobs}>
            <>
              {/* Bulk action toolbar (#5, #6) */}
              {selectedJobIds.size > 0 && (
                <div className="flex items-center gap-3 rounded-xl bg-primary/10 border border-primary/20 px-4 py-2.5">
                  <span className="text-sm font-semibold text-primary">{selectedJobIds.size} selected</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      onClick={exportSelectedCsv}
                      className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span className="material-symbols-outlined text-base">download</span>
                      Export CSV
                    </button>
                    <button
                      onClick={clearSelection}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-2 py-1.5"
                    >
                      <span className="material-symbols-outlined text-base">close</span>
                      Clear
                    </button>
                  </div>
                </div>
              )}
              <JobsTable
                jobs={displayedJobs}
                loading={loading}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                onEdit={handleEdit}
                onEditSchedule={handleEditSchedule}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                selectedIds={selectedJobIds}
                onSelectionChange={setSelectedJobIds}
              />
              {/* Pagination */}
              {!loading && totalItems > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              )}
            </>
          </PullToRefresh>
        )}
      </div>

      {/* New Job Scheduling Form */}
      <JobSchedulingForm
        isOpen={showNewJobOverlay}
        onClose={() => setShowNewJobOverlay(false)}
        onSubmit={handleNewJobSubmit}
        isSubmitting={creating}
      />

      {/* Edit Job Schedule Form */}
      {editingScheduleJob && (
        <JobSchedulingForm
          isOpen={showEditScheduleModal}
          onClose={() => {
            setShowEditScheduleModal(false);
            setEditingScheduleJob(null);
          }}
          onSubmit={handleUpdateJobSchedule}
          isSubmitting={creating}
          initialData={editingScheduleJob}
          isEditMode={true}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {confirmDeleteJob && (
        <ConfirmDialog
          title="Delete Job"
          message={`Are you sure you want to delete job ${confirmDeleteJob.jobNumber}? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteJobAction}
          onCancel={() => setConfirmDeleteJob(null)}
        />
      )}
    </MainLayout>
  );
};


